/**
 * Legislator AG Grid column definitions factory.
 *
 * Returns ColDef[] based on which columns are requested and the display mode.
 * Cell renderers are imported from the shared dataGridCells module.
 */

import { type ColDef } from 'ag-grid-community';
import {
  NameCellRenderer,
  PartyCellRenderer,
  ChamberCellRenderer,
  DistrictCellRenderer,
  ContactCellRenderer,
  PositionCellRenderer,
  NotesCellRenderer,
  CommitteesCellRenderer,
  SponsoredBillsCellRenderer,
  WorkspacesCellRenderer,
  ActionsCellRenderer,
} from '../shared/dataGridCells';
import { ActionsGridHeader } from '../ActionsHeaderHelp';
import { LegislatorTableColumn } from './LegislatorTable';
import { formatPartyDisplay } from '../../utils/partyUtils';

const HEADER_CLASS = 'ag-data-grid-header-uppercase';

export interface LegislatorColumnOptions {
  columns: LegislatorTableColumn[];
  mode?: 'directory' | 'workspace' | 'org-report';
}

export const LEGISLATOR_SORT_FIELD_MAP: Record<string, string> = {
  name: 'name',
  chamber: 'chamber',
  district: 'district',
  party: 'party',
  sponsored_bills: 'sponsored_bills_count',
};

export function buildLegislatorColumns({ columns }: LegislatorColumnOptions): ColDef[] {
  const cols: ColDef[] = [];

  for (const col of columns) {
    switch (col) {
      case 'name':
        cols.push({
          colId: 'name',
          headerName: 'NAME',
          pinned: 'left',
          width: 200,
          minWidth: 160,
          sortable: true,
          suppressMovable: true,
          headerClass: HEADER_CLASS,
          cellRenderer: NameCellRenderer,
          valueGetter: (p: any) => {
            const d = p.data;
            if (!d) return '';
            const last = d.lastName || d.last_name || '';
            const first = d.firstName || d.first_name || '';
            return (last && first) ? `${last}, ${first}` : d.fullName || d.full_name || '';
          },
        });
        break;

      case 'chamber':
        cols.push({
          colId: 'chamber',
          headerName: 'CHAMBER',
          flex: 1,
          minWidth: 90,
          sortable: true,
          headerClass: HEADER_CLASS,
          cellRenderer: ChamberCellRenderer,
          valueGetter: (p: any) => p.data?.chamber || '',
        });
        break;

      case 'district':
        cols.push({
          colId: 'district',
          headerName: 'DISTRICT',
          flex: 1,
          minWidth: 80,
          sortable: true,
          headerClass: HEADER_CLASS,
          cellRenderer: DistrictCellRenderer,
          valueGetter: (p: any) => p.data?.district || '',
        });
        break;

      case 'party':
        cols.push({
          colId: 'party',
          headerName: 'PARTY',
          flex: 1,
          minWidth: 90,
          sortable: true,
          headerClass: HEADER_CLASS,
          cellRenderer: PartyCellRenderer,
          valueGetter: (p: any) => formatPartyDisplay(p.data?.partyAffiliation || p.data?.party_affiliation || ''),
        });
        break;

      case 'committees':
        cols.push({
          colId: 'committees',
          headerName: 'COMMITTEES',
          flex: 2,
          minWidth: 140,
          sortable: false,
          headerClass: HEADER_CLASS,
          autoHeight: true,
          wrapText: true,
          cellRenderer: CommitteesCellRenderer,
          valueGetter: (p: any) => p.data?.committees || '',
        });
        break;

      case 'contact':
        cols.push({
          colId: 'contact',
          headerName: 'CONTACT',
          flex: 2,
          minWidth: 140,
          sortable: false,
          headerClass: HEADER_CLASS,
          autoHeight: true,
          cellRenderer: ContactCellRenderer,
        });
        break;

      case 'sponsored_bills':
        cols.push({
          colId: 'sponsored_bills',
          headerName: 'BILLS',
          flex: 1,
          minWidth: 80,
          sortable: true,
          headerClass: HEADER_CLASS,
          valueGetter: (p: any) => p.data?.sponsoredBillsCount ?? p.data?.sponsored_bills_count ?? 0,
          cellRenderer: SponsoredBillsCellRenderer,
        });
        break;

      case 'position':
        cols.push({
          colId: 'position',
          headerName: 'POSITION',
          flex: 1,
          minWidth: 100,
          sortable: false,
          headerClass: HEADER_CLASS,
          cellRenderer: PositionCellRenderer,
        });
        break;

      case 'notes':
        cols.push({
          colId: 'notes',
          headerName: 'NOTES',
          flex: 1,
          minWidth: 60,
          sortable: false,
          headerClass: HEADER_CLASS,
          cellRenderer: NotesCellRenderer,
        });
        break;

      case 'actions':
        cols.push({
          colId: 'actions',
          headerName: 'ACTIONS',
          // 100px fit the bare label; the legend trigger needs the same bump
          // the initiatives grid took (label + 20px cell padding + the ? icon).
          width: 130,
          minWidth: 110,
          sortable: false,
          suppressMovable: true,
          headerClass: HEADER_CLASS,
          headerComponent: ActionsGridHeader,
          cellRenderer: ActionsCellRenderer,
        });
        break;

      case 'workspaces':
        cols.push({
          colId: 'workspaces',
          headerName: 'WORKSPACES',
          flex: 2,
          minWidth: 160,
          sortable: false,
          headerClass: HEADER_CLASS,
          autoHeight: true,
          cellRenderer: WorkspacesCellRenderer,
        });
        break;

      default:
        break;
    }
  }

  return cols;
}
